import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { useState } from "react";
import { ContentLibraryLoadingModal } from "../../plugins/toolbar/content-library/content-library-loading-modal";
// oxlint-disable-next-line import/no-cycle -- Content Library modal and link form render each other at runtime (insert as link opens LinkForm, whose Content Library field opens the modal); breaking it needs dependency injection
import { ContentLibraryModal } from "../../plugins/toolbar/content-library/content-library-modal";

import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import type { Asset } from "../../../../assets/server/digital-asset-service";
import type { FC } from "react";
import type { FieldError } from "react-hook-form";

export const ButtonFormContentLibraryLink: FC<{
  assetsLoader: UseSupabaseLoaderState<Asset[]> & { reload: () => void };
  onUseAsLink: (url: string, title: string, asset: Asset) => void;
  onRemoveLink: () => void;
  formError?: FieldError;
  useRawAssetPublicUrl?: boolean;
  selectedAssetTitle?: string | null;
  disabled?: boolean;
  buttonVariant?: "text" | "outlined" | "contained";
}> = ({
  assetsLoader,
  onUseAsLink,
  onRemoveLink,
  formError,
  useRawAssetPublicUrl,
  selectedAssetTitle,
  disabled,
  buttonVariant = "text",
}) => {
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [localAssetTitle, setLocalAssetTitle] = useState<string | null>(null);
  const assetTitle = selectedAssetTitle === undefined ? localAssetTitle : selectedAssetTitle;

  return (
    <>
      {assetTitle ? (
        <Chip
          variant="filled"
          color="primary"
          key={assetTitle}
          label={assetTitle}
          onDelete={() => {
            onRemoveLink();
            setLocalAssetTitle(null);
          }}
          disabled={disabled}
          sx={{ width: "auto", alignSelf: "flex-start" }}
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Button
            data-analytics-id="button-form-select-file"
            variant={buttonVariant}
            onClick={() => {
              setLibraryOpen(true);
            }}
            disabled={disabled}
            sx={{ width: "auto", alignSelf: "flex-start" }}
          >
            Select file from Content Library
          </Button>
          {formError && (
            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
              Ensure you select a file
            </Typography>
          )}
        </Box>
      )}
      <ContentLibraryLinkModal
        open={libraryOpen}
        onClose={() => {
          setLibraryOpen(false);
        }}
        onUseAsLink={(url, title, asset) => {
          setLocalAssetTitle(title);
          onUseAsLink(url, title, asset);
        }}
        assetsLoader={assetsLoader}
        useRawAssetPublicUrl={useRawAssetPublicUrl}
      />
    </>
  );
};

const ContentLibraryLinkModal: FC<{
  open: boolean;
  onClose: () => void;
  onUseAsLink: (url: string, title: string, asset: Asset) => void;
  assetsLoader: UseSupabaseLoaderState<Asset[]> & { reload: () => void };
  useRawAssetPublicUrl?: boolean;
}> = ({ open, onClose, onUseAsLink, assetsLoader, useRawAssetPublicUrl }) => {
  const [lastUploadFolderId, setLastUploadFolderId] = useState<string | null>(null);
  const { data: assets, error: assetsError, reload: reloadAssets, loading } = assetsLoader;

  if (assetsError)
    return <Alert severity="error">Error loading Content Library. Please try again later.</Alert>;

  if (loading) return <ContentLibraryLoadingModal open={open} onClose={onClose} />;

  return (
    <ContentLibraryModal
      open={open}
      onClose={onClose}
      assets={assets ?? []}
      onUseAsLink={onUseAsLink}
      onAssetUploaded={(folderId) => {
        if (folderId) setLastUploadFolderId(folderId);
        reloadAssets();
      }}
      initialFolderId={lastUploadFolderId}
      useRawAssetPublicUrlAsLink={useRawAssetPublicUrl}
    />
  );
};
